import { useQueryClient } from "@tanstack/react-query";
import { Power } from "lucide-react";
import { useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { soundQueryKey } from "./api";
import {
  buildDisabledVoiceCatalogDraftValues,
  buildEnabledVoiceCatalogDraftValues,
} from "./catalogDraft";
import {
  formatSoundCatalogMutationError,
  publishSoundCatalogChange,
} from "./catalogWorkflow";
import type { SoundVoice } from "./contracts";

type VoiceCatalogToggleDialogProps = {
  environment: "staging" | "stg";
  mode: "enable" | "disable";
  voice: SoundVoice;
};

type ToggleState = "idle" | "saving" | "readback_pending" | "error";

export function VoiceCatalogToggleDialog({
  environment,
  mode,
  voice,
}: VoiceCatalogToggleDialogProps) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [ticketReference, setTicketReference] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [state, setState] = useState<ToggleState>("idle");
  const [message, setMessage] = useState("");
  const queryClient = useQueryClient();
  const displayName = voice.catalog?.display_name ?? voice.name;
  const enabling = mode === "enable";
  const actionLabel = enabling ? "Turn on" : "Turn off";
  const canSubmit =
    confirmed &&
    reason.trim().length >= 8 &&
    ticketReference.trim().length >= 3 &&
    (state === "idle" || state === "error");

  const reset = () => {
    setReason("");
    setTicketReference("");
    setConfirmed(false);
    setState("idle");
    setMessage("");
  };

  const publish = async () => {
    if (!canSubmit) return;
    setState("saving");
    setMessage("");
    try {
      await publishSoundCatalogChange({
        buildValues: (activeValues) =>
          enabling
            ? buildEnabledVoiceCatalogDraftValues(activeValues, voice.voice_id)
            : buildDisabledVoiceCatalogDraftValues(
                activeValues,
                voice.voice_id,
              ),
        reason: reason.trim(),
        ticketReference: ticketReference.trim(),
      });
      setState("readback_pending");
      setMessage(
        `The signed release was published. The voice will show as ${
          enabling ? "Live" : "Turned off"
        } once the system confirms the update, usually within a minute.`,
      );
      await queryClient.invalidateQueries({ queryKey: soundQueryKey });
    } catch (error) {
      setState("error");
      setMessage(formatSoundCatalogMutationError(error));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant={
            !enabling && voice.catalog_state === "orphaned"
              ? "destructive"
              : "outline"
          }
          title={`${actionLabel} ${displayName}`}
          aria-label={`${actionLabel} ${displayName}`}
        >
          <Power className="mr-1 size-3" />
          {actionLabel}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {actionLabel} {displayName}?
          </DialogTitle>
          <DialogDescription>
            {enabling
              ? "This creates an immutable RDS catalog release with the voice enabled for new Echoes. It does not add or restore anything in ElevenLabs."
              : "This creates an immutable RDS catalog release with the voice disabled. It does not delete anything from ElevenLabs."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border p-3">
            <p className="font-medium">{displayName}</p>
            <p className="font-mono text-xs text-muted-foreground">
              {voice.voice_id}
            </p>
            {enabling &&
            (voice.provider_state === "missing" ||
              voice.provider_state === "unavailable") ? (
              <p className="mt-2 text-sm text-destructive">
                ElevenLabs is not currently returning this voice. Publication
                may be rejected, and a voice that is unavailable at render time
                cannot narrate new Echoes.
              </p>
            ) : null}
            {!enabling &&
            (voice.provider_state === "missing" ||
              voice.provider_state === "unavailable") ? (
              <p className="mt-2 text-sm text-destructive">
                ElevenLabs no longer returns this voice. It remains eligible for
                new Echoes until this catalog change is published.
              </p>
            ) : null}
          </div>

          <div className="space-y-1">
            <Label htmlFor={`${mode}-reason-${voice.voice_id}`}>Reason</Label>
            <Input
              id={`${mode}-reason-${voice.voice_id}`}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={`Why this voice should be ${enabling ? "turned on" : "turned off"}`}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`${mode}-ticket-${voice.voice_id}`}>
              Ticket/reference
            </Label>
            <Input
              id={`${mode}-ticket-${voice.voice_id}`}
              value={ticketReference}
              onChange={(event) => setTicketReference(event.target.value)}
              placeholder="SND-..."
            />
          </div>
          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              className="mt-0.5"
              checked={confirmed}
              onCheckedChange={(checked) => setConfirmed(checked === true)}
            />
            <span>
              {enabling
                ? "I understand this makes the voice available for selection for new Echoes after publication. Existing Echoes keep their frozen configuration."
                : "I understand this removes the voice from selection for new Echoes after publication. Existing Echoes keep their frozen configuration."}
            </span>
          </label>

          {message ? (
            <Alert variant={state === "error" ? "destructive" : "default"}>
              <AlertTitle>
                {state === "error" ? "Voice change failed" : "Published"}
              </AlertTitle>
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          ) : null}

          <p className="text-xs text-muted-foreground">
            {environment === "stg"
              ? "CRM administrators publish this production change immediately after confirmation."
              : "Engineering staging changes are published immediately after validation."}
          </p>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
          >
            Close
          </Button>
          <Button
            type="button"
            variant={enabling ? "default" : "destructive"}
            disabled={!canSubmit}
            onClick={publish}
          >
            {state === "saving"
              ? "Saving..."
              : `Publish and ${actionLabel.toLocaleLowerCase()}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
